import { FC, ReactNode } from "react";
import { useSelector } from "react-redux";

import { Check, Edit, Error, Warning } from "@livechat/design-system-icons";
import { Button, Icon, Info, Loader, SpacingToken, Tag, Text, Tooltip } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { EmailDomainStatus, IEmailDomain } from "store/entities/email-domains";
import { selectReplyAddresses } from "store/entities/reply-addresses/selectors";
import { withVar } from "themes/helpers";

import { trackDomainsEvent } from "../../analytics";
import { DomainSetupAction, DomainSetupActionType } from "../DomainSetupModal";

interface IDomainStatusProps {
  domain: IEmailDomain;
  setModalAction: (action: DomainSetupAction | null) => void;
  canManageDomains: boolean;
}

export const DomainStatus: FC<IDomainStatusProps> = ({ domain, setModalAction, canManageDomains }) => {
  const replyAddresses = Object.values(useSelector(selectReplyAddresses));
  const unused = !replyAddresses.some(address => address.emailDomainID === domain.id);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSetupContinue = () => {
    setModalAction({ type: DomainSetupActionType.FinishSetup, domainId: domain.id });
    trackDomainsEvent("Finish adding clicked");
  };

  const confirmingDomainLoader = (
    <Loader
      size="small"
      // @ts-expect-error TS(2322)
      label={
        <Block maxWidth="400px" data-testid="confirming-loader">
          <Text size="sm" bold>
            Confirming domain...
          </Text>
          <Text size="sm">
            This may take a couple of hours. You&apos;ll receive an email confirmation when the domain is finally added.
          </Text>
        </Block>
      }
    />
  );

  switch (domain.status) {
    case EmailDomainStatus.Active:
      return (
        <DomainStatusTooltip unused={unused}>
          <Flex column gap={withVar(SpacingToken.Spacing2)} alignItems="flex-start">
            <Tag
              kind="success"
              leftNode={<Icon source={Check} size="small" />}
              size="small"
              data-testid="domain-added-tag"
            >
              Added
            </Tag>
            {unused && (
              <Tag
                kind="warning"
                size="small"
                leftNode={<Icon source={Warning} size="small" />}
                data-testid="domain-unused-tag"
              >
                Not used in reply addresses
              </Tag>
            )}
          </Flex>
        </DomainStatusTooltip>
      );
    case EmailDomainStatus.New:
    case EmailDomainStatus.PendingConfiguration:
      if (domain.name.endsWith(".helpdesk.com")) {
        return confirmingDomainLoader;
      }

      return canManageDomains ? (
        <Button
          kind="link"
          icon={<Icon source={Edit} size="small" />}
          onClick={handleSetupContinue}
          data-testid="finish-adding-button"
        >
          Finish adding
        </Button>
      ) : (
        <>-</>
      );
    case EmailDomainStatus.PendingVerification:
      return confirmingDomainLoader;
    case EmailDomainStatus.PendingDeletion:
      return (
        <Block data-testid="deleting-loader">
          <Loader
            size="small"
            // @ts-expect-error TS(2322)
            label={
              <Text size="sm" bold>
                Deleting...
              </Text>
            }
          />
        </Block>
      );
    case EmailDomainStatus.SetupFailed:
      return (
        <Tag
          kind="error"
          leftNode={<Icon source={Error} size="small" />}
          size="small"
          data-testid="domain-setup-failed-tag"
        >
          Setup failed
        </Tag>
      );
    default:
      return null;
  }
};

const DomainStatusTooltip: FC<{ children: ReactNode; unused: boolean }> = ({ children, unused }) => {
  return (
    <Tooltip kind="invert" placement="top" triggerRenderer={() => children}>
      <Info
        theme="invert"
        text={
          unused
            ? "The domain is confirmed and added properly but has not been used in your reply address yet."
            : "The domain is confirmed, added properly, and used in your reply address."
        }
      />
    </Tooltip>
  );
};
